import React, { useEffect, useMemo, useRef, useState } from 'react';
import CalendarInspectionTypeFilter from './CalendarInspectionTypeFilter';

import { Terms } from '@stormwater-software/lib-domain';
import { useTerminology } from '../../../../hooks/useTerminology';
export type FilterGroup = {
  id: number | null;
  name: string;
  projectCount: number;
};

export type InspectionTypeOption = {
  id: number;
  name: string;
  projectCount: number;
};

export type FilterGroups = {
  totalProjects: number;
  /** A filter, not a grouping — see the note on the section below. */
  inspectionTypes?: InspectionTypeOption[];
  groups: {
    client: FilterGroup[];
    area: FilterGroup[];
    projectType: FilterGroup[];
    inspector: FilterGroup[];
  };
};

export type CalendarProject = {
  id: number;
  name: string;
  client?: { id: number; name: string } | null;
  area_id?: number | null;
  project_type_id?: number | null;
  inspector_user_id?: number | null;
};

type GroupingKey = keyof FilterGroups['groups'];

interface Props {
  projects: CalendarProject[];
  filterGroups: FilterGroups | null;
  selected: number[];
  onChange: (ids: number[]) => void;
  selectedInspectionTypes: number[];
  onInspectionTypesChange: (ids: number[]) => void;
  /** The groups request failed — distinct again from "still loading". */
  groupsFailed?: boolean;
}

/**
 * SW2-2325 — project filter for the calendar widget.
 *
 * Replaces a single `<select>`. Three constraints from the research shaped this, all of
 * them consequences of real division sizes rather than taste:
 *
 * - **Groups render collapsed.** The largest division holds 1,456 active projects across
 *   136 clients; expanding everything by default is unusable.
 * - **Members render only once a group is expanded**, and only the first ten, because a
 *   single area can hold several hundred projects.
 * - **Pills stop at three.** Past that the summary collapses to a sentence, or the
 *   selection eats the widget.
 *
 * Grouping defaults to **client**, not area. Only 2,248 of 9,236 projects carry an
 * `area_id` and several divisions have none at all, so an area-first panel would open to
 * a single "Unassigned" bucket almost everywhere. Every project has a client.
 */
const getGroupingLabels = (terms: Terms): Record<GroupingKey, string> => ({
  client: terms.entity,
  area: 'Area',
  projectType: 'Project type',
  inspector: 'Inspector',
});

const MEMBERS_SHOWN = 10;

const CalendarFilterPanel: React.FC<Props> = ({
  projects,
  filterGroups,
  selected,
  onChange,
  selectedInspectionTypes,
  onInspectionTypesChange,
  groupsFailed = false,
}) => {
  const terms = useTerminology();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [grouping, setGrouping] = useState<GroupingKey>('client');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAllIn, setShowAllIn] = useState<Set<string>>(new Set());

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // The panel overlays the calendar grid, so it has to close on an outside click —
  // otherwise the only way back to the month is to find the summary button again.
  useEffect(() => {
    if (!open) return undefined;

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentMouseDown);

    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  /** Which group a project belongs to, for the active grouping. */
  const groupIdOf = (project: CalendarProject): number | null => {
    switch (grouping) {
      case 'client':
        return project.client?.id ?? null;
      case 'area':
        return project.area_id ?? null;
      case 'projectType':
        return project.project_type_id ?? null;
      case 'inspector':
        return project.inspector_user_id ?? null;
      default:
        return null;
    }
  };

  // Search narrows the projects, and a group is then shown only if something inside it
  // still matches. Ticking a group must select what the search still shows, not the
  // whole group — otherwise a search-then-select quietly picks up hidden projects.
  const visibleProjects = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return projects;

    return projects.filter((p) => p.name?.toLowerCase().includes(term));
  }, [projects, search]);

  const membersByGroup = useMemo(() => {
    const map = new Map<string, CalendarProject[]>();

    visibleProjects.forEach((project) => {
      const key = String(groupIdOf(project) ?? 'unassigned');
      const bucket = map.get(key);

      if (bucket) bucket.push(project);
      else map.set(key, [project]);
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjects, grouping]);

  const groups = filterGroups?.groups?.[grouping] ?? [];

  const toggleProject = (id: number) => {
    const next = new Set(selectedSet);

    if (next.has(id)) next.delete(id);
    else next.add(id);

    onChange(Array.from(next));
  };

  const toggleGroup = (members: CalendarProject[]) => {
    const ids = members.map((m) => m.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);

    ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
    onChange(Array.from(next));
  };

  const toggleExpanded = (key: string) => {
    const next = new Set(expanded);

    if (next.has(key)) next.delete(key);
    else next.add(key);

    setExpanded(next);
  };

  const selectedProjects = useMemo(
    () => projects.filter((p) => selectedSet.has(p.id)),
    [projects, selectedSet],
  );

  // Up to three names, then a sentence. Beyond three the pills wrap and push the
  // calendar down the widget.
  const summary = () => {
    const projectPart = !selected.length
      ? `All projects${filterGroups ? ` (${filterGroups.totalProjects})` : ''}`
      : selectedProjects.length <= 3
        ? selectedProjects.map((p) => p.name).join(', ')
        : `${selected.length} of ${filterGroups?.totalProjects ?? projects.length} projects`;

    if (!selectedInspectionTypes.length) return projectPart;

    // An active type filter has to appear here. It narrows the calendar as much as the
    // project selection does, and a panel that hides it leaves the user looking at a
    // month that seems to be missing inspections.
    const names = (filterGroups?.inspectionTypes ?? [])
      .filter((t) => selectedInspectionTypes.includes(t.id))
      .map((t) => t.name);

    const typePart =
      names.length <= 2
        ? names.join(', ')
        : `${names.length} inspection types`;

    return `${projectPart} · ${typePart}`;
  };


  const inspectionTypes = filterGroups?.inspectionTypes ?? [];

  return (
    <div className='calendar-filter' ref={rootRef}>
      <button
        type='button'
        className='calendar-filter__summary'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className='calendar-filter__summary-text'>{summary()}</span>
        <span className='calendar-filter__caret'>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className='calendar-filter__panel'>
          <div className='calendar-filter__controls'>
            <input
              type='text'
              className='calendar-filter__search'
              // The placeholder disappears as soon as anyone types, so it is not a
              // name assistive technology can rely on.
              aria-label='Search projects'
              placeholder='Search projects'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className='calendar-filter__grouping'
              value={grouping}
              onChange={(e) => {
                setGrouping(e.target.value as GroupingKey);
                setExpanded(new Set());
                setShowAllIn(new Set());
              }}
            >
              {Object.entries(getGroupingLabels(terms)).map(([key, label]) => (
                <option key={key} value={key}>
                  Group by {label}
                </option>
              ))}
            </select>
          </div>

          <div className='calendar-filter__actions'>
            {/* One control, not two: "Show all" and "Clear selection" were the same
                action, and with nothing selected the calendar is already showing all. */}
            {selected.length > 0 ? (
              <button type='button' onClick={() => onChange([])}>
                Clear selection ({selected.length}) — show all
              </button>
            ) : (
              <span className='calendar-filter__empty'>
                Showing every project in the division
              </span>
            )}
          </div>

          <CalendarInspectionTypeFilter
            types={inspectionTypes}
            selected={selectedInspectionTypes}
            onChange={onInspectionTypesChange}
          />

          <div className='calendar-filter__groups'>
            {/* `null` is "the request has not come back", `[]` is "there are none" —
                collapsing them told a user with a slow connection their division was
                empty. */}
            {!filterGroups && groupsFailed && (
              <p className='calendar-filter__empty'>
                Could not load projects. The calendar below is unaffected.
              </p>
            )}

            {!filterGroups && !groupsFailed && (
              <p className='calendar-filter__empty'>Loading projects…</p>
            )}

            {filterGroups && groups.length === 0 && (
              <p className='calendar-filter__empty'>No groups available.</p>
            )}

            {groups.map((group) => {
              const key = String(group.id ?? 'unassigned');
              const members = membersByGroup.get(key) ?? [];

              // With a search active, hide groups nothing matches in.
              if (search.trim() && members.length === 0) return null;

              const isExpanded = expanded.has(key);
              const selectedHere = members.filter((m) =>
                selectedSet.has(m.id),
              ).length;
              const shown = showAllIn.has(key)
                ? members
                : members.slice(0, MEMBERS_SHOWN);

              return (
                <div className='calendar-filter__group' key={key}>
                  <div className='calendar-filter__group-header'>
                    <button
                      type='button'
                      className='calendar-filter__group-toggle'
                      onClick={() => toggleExpanded(key)}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? '−' : '+'}
                    </button>
                    <label className='calendar-filter__group-label'>
                      <input
                        type='checkbox'
                        checked={
                          members.length > 0 && selectedHere === members.length
                        }
                        // Partially selected reads as neither on nor off.
                        ref={(el) => {
                          if (el) {
                            el.indeterminate =
                              selectedHere > 0 && selectedHere < members.length;
                          }
                        }}
                        onChange={() => toggleGroup(members)}
                      />
                      <span className='calendar-filter__group-name'>
                        {group.name}
                      </span>
                    </label>
                    <span className='calendar-filter__group-count'>
                      {selectedHere > 0
                        ? `${selectedHere}/${group.projectCount}`
                        : group.projectCount}
                    </span>
                  </div>

                  {isExpanded && (
                    <ul className='calendar-filter__members'>
                      {members.length === 0 && (
                        <li className='calendar-filter__empty'>
                          No projects in this group.
                        </li>
                      )}
                      {shown.map((project) => (
                        <li key={project.id}>
                          <label>
                            <input
                              type='checkbox'
                              checked={selectedSet.has(project.id)}
                              onChange={() => toggleProject(project.id)}
                            />
                            {project.name}
                          </label>
                        </li>
                      ))}
                      {members.length > shown.length && (
                        <li>
                          <button
                            type='button'
                            className='calendar-filter__more'
                            onClick={() =>
                              setShowAllIn(new Set(showAllIn).add(key))
                            }
                          >
                            +{members.length - shown.length} more
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarFilterPanel;
